/**
 * layout.js --- Page layouts for Articles and Editorials
 *
 * This is where the GraphQL data from the entry point is
 * sent and bussed to its appropriate page layouts.
 */

import React, { useContext } from "react"
import PageTransition from "gatsby-plugin-page-transitions"

import ThreeCanvas from "./threecanvas"
import Header from "./header/header"
import ArticleNav from "./nav/nav"
import Body from "./body/body"
import Media from "./media/media"

import { PastIssuesProvider, PastIssuesContext } from "../contexts/PastIssues"

import { MediaPopUpProvider } from "../contexts/MediaPopUp"

import GlobalStyle from "../globals.module.scss"

/**
 * Layout (default): The default layout of the site. Differentiates
 * between an article and an editorial by parsing the graphQL data
 * that is passed from the entry point (index.js). Additionally wraps
 * all global React contexts
 * 
 * Contexts: 
 * - - PastIssues: is the past issues menu open
 * - - ScrollPosition: where is the scroll position

 */
const Layout = props => {
  if (props.issue) {
    return (
      <PastIssuesProvider>
        <Editorial
          issue={props.issue}
          archiveMenuData={props.archiveMenuData}
        />
      </PastIssuesProvider>
    )
  } else if (props.article) {
    return (
      <MediaPopUpProvider>
        <PastIssuesProvider>
          <Article
            article={props.article}
            archiveMenuData={props.archiveMenuData}
          />
        </PastIssuesProvider>
      </MediaPopUpProvider>
    )
  }
}

export default Layout

/* ===================================================*/

/**
 * Editorial: The default layout for an editorial page
 */
const Editorial = props => {
  const { issue } = props

  // Past issues menu context for body opacity styling
  const [pastIssuesVisibility] = useContext(PastIssuesContext)

  return (
    <>
      <Header archiveMenuData={props.archiveMenuData} />
      <PageTransition>
        <ThreeCanvas
          backgroundColor={issue.color}
          opacity={pastIssuesVisibility === true ? "0.3" : "1"}
        />
        <div
          className={GlobalStyle.scrollContainer}
          style={{
            transition: "0.2s",
            opacity: pastIssuesVisibility === true ? "0.4" : "1",
          }}
        >
          <ArticleNav
            issueTitle={issue.title}
            slug={issue.slug}
            articles={issue.articles}
          />
          <Body
            contentType={"Editorial"}
            title={issue.editorialTitle}
            slug={issue.slug}
            date={issue.date}
            dropcap={issue.dropcap}
            content={issue.editorial.json}
            footer={issue.signature.json}
          />
        </div>
      </PageTransition>
    </>
  )
}

/**
 * Article: The default layout for an article page
 */
const Article = props => {
  const { article } = props

  // Past issues menu context for body opacity styling
  const [pastIssuesVisibility] = useContext(PastIssuesContext)

  return (
    // TODO 2: Refactor body by passing entire article prop
    <>
      <Header archiveMenuData={props.archiveMenuData} />
      <ThreeCanvas
        backgroundColor={article.color}
        opacity={pastIssuesVisibility === true ? "0.2" : "1"}
      />
      <PageTransition>
        <div
          className={GlobalStyle.scrollContainer}
          style={{
            transition: "0.2s",
            opacity: pastIssuesVisibility === true ? "0.4" : "1",
          }}
        >
          <Media
            color={article.color}
            slug={article.slug}
            media={article.articleMedia}
          />
          <Body
            contentType={"Article"}
            title={article.title}
            slug={article.slug}
            subtitle={article.subtitle === null ? null : article.subtitle.json}
            color={article.color}
            author={article.author}
            date={article.date}
            dropcap={article.dropcap}
            content={article.article.json}
            footer={article.bio.json}
            headshots={article.headshot}
          />
        </div>
      </PageTransition>
    </>
  )
}
